import { app, HostClientType } from "@microsoft/teams-js";
import xlsx from "json-as-xlsx";
import { ToasterType } from '../components/shared/toaster/toaster.model';
import Config from "../config";
import { AuthService } from "../services/AuthService";
import { FileService } from "../services/FileService";
import { UserService } from "../services/UserService";
import setToast from "./toaster.helper";
import { UtilityService } from "../services/UtilityService";

export class Headers {
    label: string;
    value: string;
}

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const isTeamsAppPath = (): boolean =>
    window.location.pathname.toLowerCase().includes("/teams");


const buildWorkbookBuffer = (col: Headers[], content: any[], fileName: string) => {
    const data = [{ columns: col, content }];
    return xlsx(data, {
        fileName,
        extraLength: 4,
        writeMode: "write",
        writeOptions: {
            type: "array" as const,
            bookType: "xlsx" as const,
        },
    });
};

const shareFileIfSupported = async (file: File): Promise<"saved" | "cancelled" | "unsupported"> => {
    if (typeof navigator.canShare !== "function" || !navigator.canShare({ files: [file] })) {
        return "unsupported";
    }

    try {
        await navigator.share({ files: [file], title: file.name });
        return "saved";
    } catch (error: any) {
        if (error?.name === "AbortError") {
            return "cancelled";
        }
        return "unsupported";
    }
};

const exportForTeamsMobile = async (col: Headers[], content: any[], fileName: string, ToasterMessage) => {
    const buffer = buildWorkbookBuffer(col, content, fileName);
    if (!buffer) {
        return;
    }

    const fullFileName = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
    const blob = new Blob([new Uint8Array(buffer as ArrayLike<number>)], { type: XLSX_MIME });
    const file = new File([blob], fullFileName, { type: XLSX_MIME });

    const shareResult = await shareFileIfSupported(file);
    if (shareResult === "saved") {
        setToast(ToasterMessage, ToasterType.SUCCESS);
        return;
    }
    if (shareResult === "cancelled") {
        return;
    }

    const formData = new FormData();
    formData.append("file", blob, fullFileName);

    const uploadResponse = await FileService.UploadNewFile(formData, fullFileName, XLSX_MIME);
    const fileId = uploadResponse?.data?.id;
    const token = AuthService.getToken()?.access_token;

    if (!fileId || !token || !Config.API_URL) {
        setToast("Unable to prepare download in Teams mobile. Please try from Teams desktop or a browser.", ToasterType.DANGER);
        return;
    }

    const params = new URLSearchParams({
        fileId: String(fileId),
        fileName: fullFileName,
        token,
        apiUrl: Config.API_URL,
    });

    const phoneNumber = UserService.getUserPhoneNumber();
    const phoneNumberId = UserService.getUserPhoneNumberId();
    if (phoneNumber) {
        params.set("phoneNumber", phoneNumber);
    }
    if (phoneNumberId) {
        params.set("phoneNumberId", String(phoneNumberId));
    }

    const helperUrl = `${window.location.origin}/teams-file-download.html#${params.toString()}`;
    await app.openLink(helperUrl);
    setToast(ToasterMessage, ToasterType.SUCCESS);
};

export const exportToExcel = (col: Headers[], content: any[], fileName: string, ToasterMessage) => {
    if (UtilityService.isTeamsMobileClient()) {
        return exportForTeamsMobile(col, content, fileName, ToasterMessage).catch((error) => {
            console.error("Teams mobile Excel export failed", error);
            setToast("Unable to download report in Teams mobile. Please try from Teams desktop or a browser.", ToasterType.DANGER);
        });
    }

    const data = [{ columns: col, content }];
    xlsx(data, {
        fileName,
        extraLength: 4,
    });
    setToast(ToasterMessage, ToasterType.SUCCESS);
}
